import React, { useState } from 'react';
import cn from 'classnames';
import CommentHistory from './CommentHistory';
import CommentInput from './CommentInput';
import { getCurrentUser } from '../../services/authentication.service';
import { CommentableEntityType } from '../../Models/trips/trip-comment.model';
import { useTripComments } from '../../shared/hooks/useTripComments';

interface TripCommentBoxProps {
  tripId: string;
  entityType: CommentableEntityType;
  entityId?: string;
  initialCollapsed?: boolean;
  title?: string;
  onClose?: () => void;
  showBorder?: boolean;
}

const TripCommentBox: React.FC<TripCommentBoxProps> = ({
  tripId,
  entityType,
  entityId,
  initialCollapsed = true,
  title,
  onClose,
  showBorder = true
}) => {
  // Generate unique storage key based on entity
  const storageKey = `comment-box-${entityType}-${entityId || 'root'}-${tripId}`;

  // Initialize collapsed state from localStorage or use initialCollapsed
  const getInitialCollapsedState = () => {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      return stored === 'true';
    }
    return initialCollapsed;
  };

  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState);
  const [submitting, setSubmitting] = useState(false);
  const currentUser = getCurrentUser();

  // Use the real-time comments hook
  const { comments, loading, error, addComment, deleteComment } =
    useTripComments(tripId, { entityType, entityId });

  const handleAddComment = async (commentText: string) => {
    if (!currentUser) {
      return;
    }

    setSubmitting(true);
    try {
      await addComment(commentText, entityType, entityId);
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(commentId);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    // Save preference to localStorage
    localStorage.setItem(storageKey, newCollapsedState.toString());
  };

  const commentCount = comments.length;

  return (
    <div
      className={cn('trip-comment-box', {
        'trip-comment-box--collapsed': isCollapsed,
        'trip-comment-box--no-border': !showBorder
      })}
    >
      {/* Collapsed state: Show comment count button */}
      {isCollapsed && (
        <button
          className='trip-comment-box__toggle'
          onClick={toggleCollapse}
        >
          <span className='trip-comment-box__icon'>💬</span>
          <span className='trip-comment-box__count'>
            {commentCount} {commentCount === 1 ? 'comment' : 'comments'}
          </span>
        </button>
      )}

      {/* Expanded state: Show full comment interface */}
      {!isCollapsed && (
        <div className='trip-comment-box__content'>
          <div className='trip-comment-box__header'>
            <h3 className='trip-comment-box__title'>
              <span className='trip-comment-box__icon'>💬</span>
              {title
                ? `${title} - Comments (${commentCount})`
                : `Comments (${commentCount})`}
            </h3>
            <div className='trip-comment-box__actions'>
              {!onClose && (
                <button
                  className='trip-comment-box__collapse'
                  onClick={toggleCollapse}
                  aria-label='Collapse comments'
                >
                  ▲
                </button>
              )}
              {onClose && (
                <button
                  className='trip-comment-box__close'
                  onClick={onClose}
                  aria-label='Close'
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {error && <div className='trip-comment-box__error'>{error}</div>}

          <CommentHistory
            comments={comments}
            currentUserId={currentUser?.user?._id || ''}
            onDelete={handleDeleteComment}
            loading={loading}
          />

          <CommentInput
            onSubmit={handleAddComment}
            disabled={submitting || !currentUser}
          />
        </div>
      )}
    </div>
  );
};

export default TripCommentBox;
